import { useRef, useCallback, useEffect } from "react";
import {
  NotificationType,
  AnyNotification,
  AUTOMATIC_STREAK_STATUS,
  FEATURE_FLAGS,
  AirdropFarmingBotStatus,
  QUEST_APP,
} from "@cede/types";
import type { AirdropFarmingBotDto } from "@cede/types";
import { Logger, QueryKeys } from "@cede/utils";
import { emitStatsDelta, type StatsDelta } from "@/hooks/data/statsSseBridge";
import { invalidateUserStats } from "@/hooks/data/userStatsCache";
import { useInvalidateInternalFeatureFlags } from "@/hooks/feature-flags";
import { emitHealthConnectionState, emitHealthFrame } from "@/hooks/health/healthSseBridge";
import type { HealthPayload } from "@/hooks/health/types";
import { useMaintenanceStatusStore } from "@/hooks/maintenance/useMaintenanceStatusStore";
import { useQuestData } from "@/hooks/quests/useQuestData";
import { useSocialAccounts } from "@/hooks/socials/useSocialAccounts";
import { invalidateShouldDisableTrade } from "@/hooks/trading/config/useAccessTradeCampaign";
import { useHubUser } from "@/hooks/user/useHubUser";
import { invalidateHubDefiAddress } from "@/hooks/user/useValidateHubDefiAddress";
import { getNotificationsSseEndpoint } from "@/utils/api";
import * as Sentry from "@sentry/nextjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useSnackbar, closeSnackbar, SnackbarKey } from "notistack";

// Minimum time between Sentry reports (2 minutes)
const SENTRY_RATE_LIMIT_MS = 2 * 60 * 1000;
// Only report after this many consecutive failures
const SENTRY_REPORT_THRESHOLD = 5;
// Base delay for exponential backoff (ms)
const BASE_RECONNECT_DELAY = 1000;
// Maximum reconnect delay (30 seconds)
const MAX_RECONNECT_DELAY = 30000;
// Maximum retry attempts before giving up
const MAX_RETRY_COUNT = 10;

/**
 * Checks if the current environment suggests SSE errors are expected/benign.
 */
const isExpectedDisconnection = (isUnmounting: boolean): boolean => {
  if (isUnmounting) return true;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return true;
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  return false;
};

/**
 * Calculate exponential backoff delay with jitter.
 */
const getBackoffDelay = (attempt: number): number => {
  const exponentialDelay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, attempt), MAX_RECONNECT_DELAY);
  const jitter = Math.random() * 0.25 * exponentialDelay;
  return exponentialDelay + jitter;
};

export const useNotifications = () => {
  const activeEventSource = useRef<EventSource | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { auth, authenticated, hubUser, ready, identityToken, identityProvider } = useHubUser();
  const { refetchSocialAccounts } = useSocialAccounts();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const isConnecting = useRef(false);
  const retryCountRef = useRef(0);
  const isUnmountingRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);
  const hadSuccessfulConnectionRef = useRef(false);
  const lastSentryReportAtRef = useRef<number>(0);
  const { setMaintenanceMode } = useMaintenanceStatusStore();
  const { invalidQuestsStatus } = useQuestData({ enabled: false });
  const invalidateInternalFeatureFlags = useInvalidateInternalFeatureFlags();

  // Normal callback with all its dependencies
  const handleNotification = useCallback(
    (notification: AnyNotification) => {
      switch (notification.type) {
        case NotificationType.PING:
          // Keep-alive event — no action needed, only keeps the SSE connection open.
          break;
        case NotificationType.WASH_TRADE:
          enqueueSnackbar(notification.message, {
            variant: "error",
            persist: false,
            autoHideDuration: 10000,
          });
          break;
        case NotificationType.SOCIAL_ACCOUNT_CONNECTED:
          refetchSocialAccounts();
          enqueueSnackbar(notification.message, { variant: "success" });
          break;
        case NotificationType.SOCIAL_ACCOUNT_DEACTIVATED:
          refetchSocialAccounts();
          enqueueSnackbar(notification.message, { variant: "error" });
          break;
        case NotificationType.QUEST_COMPLETED:
          // Hub and Arena share this stream and each has its own quest catalogue, so an
          // Arena completion must not toast here or refetch the Hub quest reads.
          if (notification.data?.app !== QUEST_APP.HUB) break;
          enqueueSnackbar(notification.message, { variant: "success" });
          invalidQuestsStatus();
          break;
        case NotificationType.SPINNING_WHEEL_RESULT:
          window.dispatchEvent(new CustomEvent("spinning-wheel-notification", { detail: notification }));
          break;
        case NotificationType.AUTOMATIC_STREAK_PROGRESS: {
          const status = notification.data?.status;
          const campaignName = notification.data?.campaignName;
          const streakId = notification.data?.streakId;

          if (streakId) {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, streakId] });
          }
          queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAKS] });

          if (status === AUTOMATIC_STREAK_STATUS.IN_PROGRESS) {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK_BALANCE] });
          }

          let variant: "success" | "error" | "info" = "info";
          if (status === AUTOMATIC_STREAK_STATUS.COMPLETED) variant = "success";
          else if (status === AUTOMATIC_STREAK_STATUS.STOPPED) variant = "error";

          enqueueSnackbar(notification.message, {
            variant,
            autoHideDuration: 8000,
            action: (snackbarId: SnackbarKey) => (
              <button
                onClick={() => {
                  closeSnackbar(snackbarId);
                  if (campaignName) router.push(`/${campaignName}?tab=autopilot`);
                }}
                className="px-3 py-1.5 text-xs font-semibold rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors duration-200"
              >
                View
              </button>
            ),
          });
          break;
        }
        case NotificationType.AIRDROP_BOT_UPDATED: {
          const botData = notification.data as AirdropFarmingBotDto | undefined;
          const TERMINAL_STATUSES: AirdropFarmingBotStatus[] = [
            AirdropFarmingBotStatus.COMPLETED,
            AirdropFarmingBotStatus.STOPPED,
            AirdropFarmingBotStatus.STOPPED_MAX_LOSS,
            AirdropFarmingBotStatus.ERROR,
          ];
          const isTerminal = botData?.status && TERMINAL_STATUSES.includes(botData.status);

          // Full cache update — replace the entire bot with SSE data
          if (botData?.id) {
            queryClient.setQueriesData<AirdropFarmingBotDto[]>(
              { queryKey: [QueryKeys.AIRDROP_FARMING_BOTS] },
              (prev) => {
                if (!prev) return prev;
                return prev.map((bot) => (bot.id === botData.id ? botData : bot));
              },
            );
          }

          // For terminal statuses, also invalidate as a safety net
          if (isTerminal) {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.AIRDROP_FARMING_BOTS] });
          }

          // Snackbar for terminal states
          if (botData?.status === AirdropFarmingBotStatus.COMPLETED) {
            enqueueSnackbar(`Bot completed! ${botData.pair} reached volume target.`, { variant: "success" });
          } else if (botData?.status === AirdropFarmingBotStatus.STOPPED_MAX_LOSS) {
            enqueueSnackbar(`Bot stopped: ${botData.pair} hit max loss limit.`, { variant: "error" });
          } else if (botData?.status === AirdropFarmingBotStatus.ERROR) {
            enqueueSnackbar(`Bot error: ${botData.pair} encountered an issue.`, { variant: "error" });
          }
          break;
        }
        case NotificationType.FEATURE_FLAG_CHANGED:
          Logger.log(`Feature flag changed: ${notification.data?.key}`, Logger.types.INFO);
          invalidateInternalFeatureFlags();

          if (notification.data?.key === FEATURE_FLAGS.MAINTENANCE_MODE_API_ENABLE) {
            const isActive = notification.data?.newValue ?? false;
            setMaintenanceMode(isActive);
            enqueueSnackbar(
              isActive
                ? "The site is currently under maintenance. Recent trades won't be lost."
                : "The site is no longer under maintenance. You can now trade again.",
              { variant: "info" },
            );
          }
          break;
        case NotificationType.EXTERNAL_ORDER_DETECTED:
          invalidateHubDefiAddress(queryClient);
          invalidateShouldDisableTrade(queryClient);
          enqueueSnackbar(notification.message, {
            variant: "error",
            persist: false,
            autoHideDuration: 10000,
          });
          break;
        case NotificationType.ARENA_BALANCE_UPDATED:
          // Shared stream; the Arena balance is only shown in the Arena app.
          break;
        case NotificationType.HEALTH:
          // API-liveness heartbeat — forward to the health bridge; the API-down
          // banner detector infers "down" from a gap in these frames.
          emitHealthFrame(notification.data as HealthPayload);
          break;
        case NotificationType.GLOBAL_STATS_DELTA:
          // Hub-only global volume/trade delta — forward to the stats bridge so
          // `useRealtimeStats` folds it into the cache.
          emitStatsDelta(notification.data as StatsDelta);
          break;
        case NotificationType.USER_STATS_UPDATED:
          invalidateUserStats(queryClient);
          break;
        default:
          Logger.error("Unknown notification type", notification);
      }
    },
    [
      enqueueSnackbar,
      router,
      queryClient,
      refetchSocialAccounts,
      invalidQuestsStatus,
      setMaintenanceMode,
      invalidateInternalFeatureFlags,
    ],
  );

  // Ref for the callback - so EventSource listeners always use latest version
  const handleNotificationRef = useRef(handleNotification);
  handleNotificationRef.current = handleNotification;

  const disconnectSse = useCallback(() => {
    if (!activeEventSource.current) return;
    activeEventSource.current.close();
    activeEventSource.current = null;
    isConnecting.current = false;
    // Health rides this socket — tell its detector the liveness feed is gone.
    emitHealthConnectionState(false);
    Logger.log("Notifications SSE disconnected", Logger.types.INFO);
  }, []);

  // Refs for values used in connectSse that shouldn't trigger reconnection
  const identityProviderRef = useRef(identityProvider);
  const identityTokenRef = useRef(identityToken);
  identityProviderRef.current = identityProvider;
  identityTokenRef.current = identityToken;

  // Ref for connectSse itself (used in setTimeout for reconnect)
  const connectSseRef = useRef<(token: string | null) => void>(() => undefined);

  const connectSse = useCallback(
    (token: string | null) => {
      if (isConnecting.current) return;
      isConnecting.current = true;

      try {
        const endpoint = getNotificationsSseEndpoint(token, identityProviderRef.current, identityTokenRef.current);
        const eventSource = new EventSource(endpoint);

        eventSource.addEventListener("open", () => {
          const isReconnect = hadSuccessfulConnectionRef.current;
          Logger.log(`Notifications SSE connected ${token ? "(authenticated)" : "(public mode)"}`, Logger.types.INFO);
          isConnecting.current = false;
          retryCountRef.current = 0;
          consecutiveErrorsRef.current = 0;
          hadSuccessfulConnectionRef.current = true;
          // Redis pub/sub does not replay events missed while this authenticated socket was disconnected.
          if (token && isReconnect) void invalidateUserStats(queryClient);
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          // Health rides this socket — tell its detector the feed is live.
          emitHealthConnectionState(true);
        });

        // Every feed rides this socket on the default `message` event —
        // notifications, the API-liveness `health` heartbeat, and Hub-only
        // `global.stats.delta` — each dispatched by `type` in handleNotification.
        eventSource.addEventListener("message", (event) => {
          try {
            const notification = JSON.parse(event.data) as AnyNotification;
            // PING (20s) and HEALTH (5s) are high-frequency keep-alives — don't log them.
            if (notification.type !== NotificationType.PING && notification.type !== NotificationType.HEALTH) {
              Logger.log(`SSE notification received: ${notification.type}`, Logger.types.INFO);
            }
            handleNotificationRef.current(notification);
          } catch (error) {
            Logger.error("Failed to parse notification", error);
          }
        });

        eventSource.addEventListener("error", (error) => {
          Logger.error("Notifications SSE error", error);
          isConnecting.current = false;

          if (isUnmountingRef.current) {
            disconnectSse();
            return;
          }

          if (eventSource.readyState === EventSource.CLOSED) {
            consecutiveErrorsRef.current += 1;
            const now = Date.now();
            const timeSinceLastReport = now - lastSentryReportAtRef.current;

            const shouldReportToSentry =
              !isExpectedDisconnection(isUnmountingRef.current) &&
              hadSuccessfulConnectionRef.current &&
              consecutiveErrorsRef.current >= SENTRY_REPORT_THRESHOLD &&
              timeSinceLastReport >= SENTRY_RATE_LIMIT_MS;

            if (shouldReportToSentry) {
              lastSentryReportAtRef.current = now;
              Sentry.captureMessage("Notifications SSE connection interrupted", {
                level: "warning",
                fingerprint: ["sse-connection-interrupted", token ? "authenticated" : "public"],
                extra: {
                  authenticated: !!token,
                  readyState: eventSource.readyState,
                  consecutiveErrors: consecutiveErrorsRef.current,
                  documentHidden: typeof document !== "undefined" ? document.visibilityState : "unknown",
                  navigatorOnline: typeof navigator !== "undefined" ? navigator.onLine : "unknown",
                },
              });
            }

            disconnectSse();

            if (retryCountRef.current < MAX_RETRY_COUNT) {
              const backoffDelay = getBackoffDelay(retryCountRef.current);
              retryCountRef.current += 1;
              Logger.log(
                `SSE closed, reconnecting in ${Math.round(backoffDelay / 1000)}s (attempt ${
                  retryCountRef.current
                }/${MAX_RETRY_COUNT})...`,
                Logger.types.INFO,
              );
              reconnectTimeoutRef.current = setTimeout(() => connectSseRef.current(token), backoffDelay);
            } else {
              Logger.error(`SSE reconnection failed after ${MAX_RETRY_COUNT} attempts, giving up`);
            }
          }
        });

        activeEventSource.current = eventSource;
      } catch (error) {
        Logger.error("Failed to create EventSource", error);
        isConnecting.current = false;

        if (isUnmountingRef.current) return;

        retryCountRef.current += 1;
        const now = Date.now();
        const timeSinceLastReport = now - lastSentryReportAtRef.current;

        if (
          !isExpectedDisconnection(isUnmountingRef.current) &&
          retryCountRef.current >= SENTRY_REPORT_THRESHOLD &&
          timeSinceLastReport >= SENTRY_RATE_LIMIT_MS
        ) {
          lastSentryReportAtRef.current = now;
          Sentry.captureMessage("Failed to create SSE EventSource", {
            level: "warning",
            fingerprint: ["sse-creation-failed", token ? "authenticated" : "public"],
            extra: {
              authenticated: !!token,
              retryCount: retryCountRef.current,
              errorMessage: error instanceof Error ? error.message : String(error),
            },
          });
        }

        if (retryCountRef.current < MAX_RETRY_COUNT) {
          const backoffDelay = getBackoffDelay(retryCountRef.current);
          Logger.log(
            `Retrying SSE EventSource creation in ${Math.round(backoffDelay / 1000)}s (attempt ${
              retryCountRef.current
            }/${MAX_RETRY_COUNT})...`,
            Logger.types.INFO,
          );
          reconnectTimeoutRef.current = setTimeout(() => connectSseRef.current(token), backoffDelay);
        } else {
          Logger.error(`Failed to create SSE EventSource after ${MAX_RETRY_COUNT} attempts, giving up`);
        }
      }
    },
    [disconnectSse, queryClient],
  );

  connectSseRef.current = connectSse;

  // Ref for disconnectSse to use in effect
  const disconnectSseRef = useRef(disconnectSse);
  disconnectSseRef.current = disconnectSse;

  const { data: sseToken } = useQuery({
    queryKey: ["notifications-sse-token", hubUser?.id, identityProvider],
    enabled: !!hubUser?.id && !!identityToken && !!identityProvider && authenticated && ready,
    queryFn: async () => {
      const token = await auth.getAccessToken();
      if (!token) throw new Error("No access token available");
      return token;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Main effect - only depends on actual state that should trigger reconnection
  useEffect(() => {
    if (!ready) {
      disconnectSseRef.current();
      return;
    }

    if (authenticated && !sseToken) {
      Logger.log("SSE waiting for authentication token...", Logger.types.INFO);
      return;
    }

    // Reset counters for fresh connection
    retryCountRef.current = 0;
    consecutiveErrorsRef.current = 0;
    isUnmountingRef.current = false;
    hadSuccessfulConnectionRef.current = false;
    lastSentryReportAtRef.current = 0;

    // Use token when authenticated, otherwise connect in public mode
    const tokenForSse = authenticated ? (sseToken ?? null) : null;
    connectSseRef.current(tokenForSse);

    const onBeforeUnload = () => {
      isUnmountingRef.current = true;
      disconnectSseRef.current();
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onBeforeUnload);

    return () => {
      isUnmountingRef.current = true;
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onBeforeUnload);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      disconnectSseRef.current();
    };
  }, [sseToken, authenticated, ready]);

  return {
    isConnected: !!activeEventSource.current,
  };
};
